import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import Logo from '../components/Logo.jsx';

/**
 * ForgotPassword
 * ----------------
 * Two channels in one flow:
 *
 *  1. SMS — enter email → backend texts a 6-digit code to the phone on file
 *           → enter the code → enter new password → done, you're signed in.
 *
 *  2. Email — enter email → backend emails a reset link → user clicks the
 *             link → /reset-password?token=... handles the new-password form.
 *
 * The backend never reveals whether an account exists. Either way, this page
 * advances to the next step and shows a generic "we sent it if it exists"
 * message. For SMS, the response includes a masked phone hint so the user
 * knows where the code went.
 */
export default function ForgotPassword() {
  const [step, setStep] = useState(1);            // 1=email+channel, 2=sms code, 3=new password (sms path)
  const [channel, setChannel] = useState('sms');  // sms | email
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [loading, setLoading] = useState(false);
  const { setSession } = useAuth();
  const nav = useNavigate();

  async function start(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await apiPost('/auth/forgot/start', { email: email.toLowerCase().trim(), channel });
      setSentTo(r?.sentTo || '');
      if (channel === 'sms') {
        setStep(2);
        toast.success(r?.sentTo ? `Code sent to ${r.sentTo}` : 'If an account exists, a code is on its way');
      } else {
        // email path — just confirm and stop here
        setStep(99);
        toast.success('Check your email for a reset link');
      }
    } catch (err) {
      // Backend always returns 200 ok for /start; only network errors land here.
      toast.error('Could not send reset. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function verify(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await apiPost('/auth/forgot/verify', { email: email.toLowerCase().trim(), code });
      if (!r?.resetToken) throw new Error(r?.error || 'Invalid code');
      setResetToken(r.resetToken);
      setStep(3);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Invalid or expired code';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  async function reset(e) {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const r = await apiPost('/auth/forgot/reset', { resetToken, password });
      if (!r?.token) throw new Error(r?.error || 'Reset failed');
      setSession(r.token, r.agent);
      toast.success('Password updated — signing you in');
      setTimeout(() => nav('/', { replace: true }), 50);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Reset failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-civix-bg relative overflow-hidden">
      <div className="absolute inset-0 bg-civix-radial pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-civix-purple/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-civix-cyan/20 rounded-full blur-3xl" />

      <div className="panel w-full max-w-md p-8 md:p-10 relative">
        <div className="mb-6"><Logo size="md" /></div>
        <h1 className="text-2xl font-display font-bold mb-1">Reset your password</h1>
        <p className="text-white/50 mb-6 text-sm">
          {step === 1 && 'Choose how you want to verify.'}
          {step === 2 && (sentTo ? `We texted a 6-digit code to ${sentTo}.` : 'Enter the 6-digit code we just sent.')}
          {step === 3 && 'Choose a new password.'}
          {step === 99 && 'If an account exists for this email, a reset link is on the way. Check your inbox (and spam).'}
        </p>

        {step === 1 && (
          <form onSubmit={start} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input type="email" className="input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="label mb-2 block">How should we send your reset?</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setChannel('sms')}
                  className={`px-3 py-3 rounded-lg border text-sm font-medium transition ${channel === 'sms' ? 'bg-civix-purple/30 border-civix-purpleSoft text-white' : 'bg-white/5 border-civix-border text-white/70 hover:bg-white/10'}`}
                >
                  Text me a code
                </button>
                <button
                  type="button"
                  onClick={() => setChannel('email')}
                  className={`px-3 py-3 rounded-lg border text-sm font-medium transition ${channel === 'email' ? 'bg-civix-purple/30 border-civix-purpleSoft text-white' : 'bg-white/5 border-civix-border text-white/70 hover:bg-white/10'}`}
                >
                  Email me a link
                </button>
              </div>
              <p className="text-[11px] text-white/40 mt-2">
                Text uses the phone number on your account. If you don't have a phone on file, choose email.
              </p>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full mt-2 disabled:opacity-60">
              {loading ? 'Sending…' : 'Send reset'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verify} className="space-y-4">
            <div>
              <label className="label">6-digit code</label>
              <input
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                className="input mt-1 tracking-[0.5em] text-center text-xl font-mono"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            </div>
            <button type="submit" disabled={loading || code.length !== 6} className="btn-primary w-full disabled:opacity-60">
              {loading ? 'Verifying…' : 'Verify code'}
            </button>
            <button
              type="button"
              onClick={() => { setStep(1); setCode(''); }}
              className="btn-ghost w-full border border-civix-border text-sm"
            >
              Use a different method
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={reset} className="space-y-4">
            <div>
              <label className="label">New password</label>
              <input type="password" minLength={6} className="input mt-1" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <div>
              <label className="label">Confirm password</label>
              <input type="password" minLength={6} className="input mt-1" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
              {loading ? 'Saving…' : 'Save & sign in'}
            </button>
          </form>
        )}

        {step === 99 && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-white/5 border border-civix-border text-sm text-white/70">
              The link expires in 60 minutes. If you don't see it within a few minutes, check your spam folder or try again with the text-message option.
            </div>
            <button onClick={() => { setStep(1); setEmail(''); setSentTo(''); }} className="btn-ghost w-full border border-civix-border text-sm">
              Try another method
            </button>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-white/50">
          <Link to="/login" className="text-civix-purpleSoft hover:text-civix-cyan">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
